import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { EmptyState } from '../ui/empty-state';
import { CheckCircle2, AlertCircle, Mic, Clock, MessageSquare } from 'lucide-react';

const ConversationQualityScorecard = ({ filters, reducedMotion }) => {
  const [loading, setLoading] = useState(true);
  const [qualityData, setQualityData] = useState(null);

  useEffect(() => {
    fetchQualityScorecard();
  }, [filters]);

  // TODO Phase Audit Fix B: wire to real /api/analytics/quality-scorecard endpoint when backend implements it.
  const fetchQualityScorecard = async () => {
    setLoading(true);
    try {
      // No real endpoint yet — leave data null so the EmptyState renders.
      setQualityData(null);
    } catch (error) {
      console.error('Failed to fetch quality scorecard:', error);
      setQualityData(null);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.9) return 'text-emerald-400';
    if (score >= 0.8) return 'text-ink-500';
    if (score >= 0.7) return 'text-amber-400';
    return 'text-red-400';
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="conversation-quality-scorecard-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100">Conversation Quality Scorecard</CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Multi-dimensional quality assessment
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-800/30 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : qualityData ? (
          <div className="space-y-6">
            {/* Overall Score */}
            <div className="text-center p-6 bg-slate-800/30 rounded-xl border border-slate-700/50">
              <p className="text-sm text-slate-400 mb-2">Overall Quality Score</p>
              <div className="flex items-center justify-center gap-3">
                <p className={`text-5xl font-bold ${getScoreColor(qualityData.overall_score)}`} data-testid="overall-quality-score">
                  {(qualityData.overall_score * 100).toFixed(0)}
                </p>
                <div className="text-left">
                  <p className="text-2xl font-bold text-slate-100">/100</p>
                  <p className="text-xs text-slate-500">{qualityData.sample_size} calls</p>
                </div>
              </div>
            </div>

            {/* Dimensions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {qualityData.dimensions.map((dim, index) => {
                const Icon = dim.icon;
                return (
                  <div
                    key={dim.name}
                    className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50"
                    style={{
                      animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.1}s both`
                    }}
                    data-testid={`dimension-${dim.name.toLowerCase()}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${dim.color}`} />
                        <h4 className="font-medium text-slate-100">{dim.name}</h4>
                      </div>
                      <Badge className={`${getScoreColor(dim.score)} bg-opacity-20`} data-testid={`score-badge-${dim.name.toLowerCase()}`}>
                        {(dim.score * 100).toFixed(0)}%
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400">{dim.description}</p>
                    
                    {/* Progress bar */}
                    <div className="mt-3 h-2 bg-slate-700/50 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${dim.color.replace('text-', 'bg-')} rounded-full transition-all duration-500`}
                        style={{ width: `${dim.score * 100}%` }}
                        data-testid={`progress-bar-${dim.name.toLowerCase()}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Insights */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-300 mb-3">Key Insights</h4>
              {qualityData.insights.map((insight, index) => {
                const Icon = insight.icon;
                const bgColor = insight.type === 'positive' ? 'bg-emerald-500/10' : 'bg-amber-500/10';
                const textColor = insight.type === 'positive' ? 'text-emerald-400' : 'text-amber-400';
                return (
                  <div key={index} className={`p-3 ${bgColor} rounded-lg border border-slate-700/30`} data-testid={`insight-${insight.type}`}>
                    <div className="flex items-start gap-2">
                      <Icon className={`w-4 h-4 ${textColor} mt-0.5`} />
                      <p className="text-sm text-slate-300">{insight.message}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={CheckCircle2}
            title="No data yet"
            description="Data will appear once it flows through."
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ConversationQualityScorecard;